// LICENSE_CODE ZON
'use strict'; /*jslint react:true es6:true*/
import React, {
    useRef,
    useMemo,
    useEffect,
    useContext,
    useCallback,
    useImperativeHandle,
} from 'react';
import cn from 'classnames';
import {Link} from 'react-router-dom';
import {Text} from '../text/text';
import {Checkbox} from '../checkbox';
import {Button} from '../button';
import {Icon} from '../icon';
import {Pagination} from '../pagination';
import {Lead_context, Columns_context, Meta_context, Pagination_context,
    Selection_context, Sort_context, Record_context, Selection_context_wrapper,
    Sort_context_wrapper, Pagination_context_wrapper, Meta_context_wrapper,
    Columns_context_wrapper} from './contexts.js';
import {get_ico} from './utils.js';
import * as cns from './table.module.less';

const Loader = ()=><Icon ico="spinner" size="huge" color="dark"/>;

export function Notice_row({children, has_items}){
    const {count} = useContext(Columns_context);
    return <tr className={has_items&&cns.row_info__has_items}>
      <td className={cn(cns.row_info)} data-test-id="table_info_row"
        colSpan={count}>
        {children}
      </td>
    </tr>;
}

export function TH({children, sorting_field, left, className}){
    const {inc, dec} = useContext(Columns_context);
    const {sorting, toggle} = useContext(Sort_context);
    const icon = get_ico(sorting_field, sorting);
    const handle_toggle = useCallback(()=>toggle(sorting_field),
        [toggle, sorting_field]);
    useEffect(()=>{
        inc();
        return ()=>dec();
    }, []);
    let content;
    if (sorting_field)
    {
        content = <Button size="s" className={cn(cns.h_button,
          !left&&cns.h_button__right)} view="link" on_click={handle_toggle}
          color_scheme="main">
          {!left && icon}
          {children}
          {left && icon}
        </Button>;
    }
    else
    {
        content = <Text.xxs bold className={cns.t_header} tertiary>
          {children}
        </Text.xxs>;
    }
    return <th className={cn(cns.hcol, !left&&cns.hcol__right, className)}>
      {content}</th>;
}

function Selector({is_header}){
    const {
        log,
        enabled,
        all_selected,
        is_selected,
        has_items,
        toggle,
        select_many,
        toggle_all,
        push_id,
        pop_id,
    } = useContext(Selection_context);
    const {list} = useContext(Sort_context);
    const {id_key} = useContext(Meta_context);
    const record = useContext(Record_context);
    const rec_id = record?.[id_key];
    useEffect(()=>{
        if (!rec_id)
            return;
        push_id(rec_id);
        return ()=>pop_id(rec_id);
    }, [rec_id]);
    const label = is_header ? 'All' : '';
    const Sel_comp = is_header ? TH : TD;
    const selected = is_header ? all_selected : is_selected(rec_id);
    const handle_change = useCallback(event=>{
        if (is_header)
            return void toggle_all();
        const shifted = event.nativeEvent?.shiftKey;
        if (shifted)
        {
            const last_action = log[log.length-1];
            if (last_action?.length==1 && last_action[0].is_add)
            {
                const last_index = list
                    .findIndex(item=>item[id_key]==last_action[0].id);
                const target_index = list
                    .findIndex(item=>item[id_key]==rec_id);
                const ids = list.slice(
                    Math.min(last_index, target_index),
                    Math.max(last_index, target_index)+1,
                ).map(item=>item[id_key]);
                return void select_many(ids);
            }
        }
        toggle(rec_id);
    }, [rec_id, is_header, toggle_all, select_many, toggle, log, list]);
    if (!enabled)
        return null;
    const has_checkbox = !!rec_id || !!is_header&&has_items;
    return <Sel_comp left className={cn(is_header&&cns.header_checkbox)}>
      {has_checkbox&&<Checkbox label={label} checked={selected}
        on_change={handle_change}/>}
    </Sel_comp>;
}

export function TR({children, sub=false, is_header, className, base_height}){
    const {get_row_link, on_row_click} = useContext(Meta_context);
    let items = children;
    if (get_row_link || on_row_click)
    {
        items = React.Children.map(children, (child, i)=>{
            if (i)
                return child;
            return <Lead_context.Provider value={true}>
              {child}</Lead_context.Provider>;
        });
    }
    const style = useMemo(()=>{
        if (!base_height)
            return;
        return {height: base_height};
    }, [base_height]);
    return <tr className={cn(cns.row, sub&&cns.row__sub, className)}
      style={style}>
      <Selector is_header={is_header}/>
      {items}
    </tr>;
}

export function TD({children, left, className, col_span}){
    const is_lead = useContext(Lead_context);
    const record = useContext(Record_context);
    const {get_row_link, on_row_click} = useContext(Meta_context);
    const url = get_row_link?.(record);
    const handle_click = useCallback(()=>{
        on_row_click?.(record);
    }, [on_row_click, record]);
    let content;
    if (url)
    {
        content = <Link className={cns.col_link} to={url}
          tabIndex={is_lead ? undefined : '-1'}>
          <div className={cns.col_link_wrap}>
            {children}
          </div>
        </Link>;
    }
    else if (on_row_click)
    {
        content = <button className={cns.col_btn} onClick={handle_click}
          tabIndex={is_lead ? undefined : '-1'}>
          {children}
        </button>;
    }
    else
        content = children;
    const classes = cn({
        [cns.col]: true,
        [cns.col__left]: left,
        [cns.col__link]: !!url,
        [cns.col__button]: !!on_row_click,
    }, className);
    return <td className={classes} colSpan={col_span}>
      {content}
    </td>;
}

export function TD_foot({children, left, className, col_span=1}){
    return <TD className={cn(cns.foot_col, className)} left={left}
      col_span={col_span}>{children}</TD>;
}

function Table_inner({
    children,
    header,
    footer,
    render_row,
    loading,
    sticky,
})
{
    const {id_key} = useContext(Meta_context);
    const {list} = useContext(Pagination_context);
    return <table className={cn(cns.inner, sticky&&cns.inner__sticky)}>
      {!!header&&<thead className={cns.head}>
        <TR is_header>{header}</TR>
      </thead>}
      <tbody>
        {list.map(it=><Record_context.Provider key={it[id_key]} value={it}>
          {render_row(it)}
        </Record_context.Provider>)}
        {children}
        {loading
          ? <Notice_row has_items={!!list?.length}>
              <div className={cns.loading_content}>
                <Loader label="Loading..."/>
              </div>
            </Notice_row>
          : null
        }
        {!loading&&!list?.length
          ? <Notice_row><Text.s tertiary>No records</Text.s></Notice_row>
          : null
        }
      </tbody>
      {!!footer&&<tfoot className={cns.foot}>{footer}</tfoot>}
    </table>;
}

function Rows_loader({loading}){
    const ref = useRef();
    const {next_page, has_more,
        infinity_scroll} = useContext(Pagination_context);
    useEffect(()=>{
        if (!ref.current || !has_more || !infinity_scroll)
            return;
        const observer = new IntersectionObserver(entries=>{
            if (!entries[0]?.isIntersecting || loading)
                return;
            next_page();
        });
        observer.observe(ref.current);
        return ()=>observer.disconnect();
    }, [loading, next_page, has_more]);
    return <div className={cns.more_row} ref={ref}>
      {has_more&&<Button className={cns.more} size="s" on_click={next_page}>
        See more</Button>}
    </div>;
}

function Table_comp({
    id_key='_id',
    sticky=false,
    header,
    footer,
    children,
    list,
    render_row,
    default_sorting,
    sorting,
    loading,
    className,
    selectable,
    get_row_link,
    on_row_click,
    on_change_selection,
    page_size,
    infinity_scroll,
    pagination,
}, ref)
{
    const selection_ref = useRef();
    useImperativeHandle(ref, ()=>({
        clear_selection: ()=>selection_ref.current?.clear(),
        set_selection: ids=>selection_ref.current?.set_selection(ids),
    }), []);
    return <div className={cn(cns.root, className)}>
      <Sort_context_wrapper default_sorting={default_sorting} list={list}
        sorting={sorting}>
        <Selection_context_wrapper enabled={selectable} ref={selection_ref}
          on_change={on_change_selection}>
          <Columns_context_wrapper>
            <Meta_context_wrapper id_key={id_key} get_row_link={get_row_link}
              on_row_click={on_row_click}>
              <Pagination_context_wrapper page_size={page_size}
                infinity_scroll={infinity_scroll}>
                <Table_inner render_row={render_row} header={header}
                  className={className}
                  loading={loading} footer={footer}
                  on_change_selection={on_change_selection} sticky={sticky}>
                  {children}
                </Table_inner>
               <Rows_loader loading={loading}/>
              </Pagination_context_wrapper>
            </Meta_context_wrapper>
          </Columns_context_wrapper>
        </Selection_context_wrapper>
      </Sort_context_wrapper>
      <Pagination className={cns.pagination} pagination={pagination}
        loading={loading}/>
    </div>;
}

export const Table = React.forwardRef(Table_comp);
